import { useState, useEffect, useRef, useCallback } from 'react'
import { AdminPanelButton } from './AdminPanelButton'
import { Badge } from '../../../components/ui/badge'
import { MessageSquare, X, User, Bot, Clock, Trash2, Eye, EyeOff } from 'lucide-react'
import { API_BASE_URL } from '../../../lib/config'
import { MarkdownMessage } from '../../../components/lib/MarkdownMessage'
import { formatISTDate } from '../../../components/lib/dateUtils'

interface Session {
  id: string
  name: string
  created_at: string
  message_count: number
  deleted_count: number
  is_deleted?: string
  deleted_at?: string
  deleted_by_user?: string
  total_input_tokens?: number
  total_output_tokens?: number
  total_cost?: number
}

interface Message {
  id: string
  role: string
  content: string
  timestamp: string
  is_deleted?: string
  is_edited?: string
  original_content?: string
  edited_at?: string
  deleted_at?: string
  input_tokens?: number | null
  output_tokens?: number | null
  cost?: number | null
}

interface UserSessionsModalProps {
  userId: string
  /** Person name for the title (e.g. first + last); falls back to username in the parent if no name */
  displayName: string
  username?: string
  userEmail: string
  onClose: () => void
  /** When set, select this session once sessions are loaded */
  initialSessionId?: string | null
  /** Single-session mode: show only message pane (no session list) */
  showSessionsList?: boolean
  /** Optional metadata for single-session mode header */
  initialSessionName?: string
  initialSessionCreatedAt?: string | null
}

const MESSAGE_PAGE_SIZE = 50
const SESSION_PAGE_SIZE = 30

type MsgPagination = {
  total: number
  hasMoreOlder: boolean
  nextOffset: number | null
}

type SessionPagination = {
  total: number
  hasMore: boolean
  nextOffset: number | null
}

export function UserSessionsModal({
  userId,
  displayName,
  username,
  userEmail,
  onClose,
  initialSessionId = null,
  showSessionsList = true,
  initialSessionName,
  initialSessionCreatedAt,
}: UserSessionsModalProps) {
  const [sessions, setSessions] = useState<Session[]>([])
  const [messages, setMessages] = useState<{ [key: string]: Message[] }>({})
  const [msgPagination, setMsgPagination] = useState<Record<string, MsgPagination>>({})
  const [sessionPagination, setSessionPagination] = useState<SessionPagination>({
    total: 0,
    hasMore: false,
    nextOffset: null,
  })
  const [selectedSession, setSelectedSession] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingMoreSessions, setLoadingMoreSessions] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [showDeletedSessions, setShowDeletedSessions] = useState(false)

  useEffect(() => {
    if (!showSessionsList) {
      if (initialSessionId) setSelectedSession(initialSessionId)
      return
    }
    void fetchSessions(0, false)
  }, [userId, showSessionsList, initialSessionId])

  const fetchMessages = useCallback(
    async (sessionId: string, offset = 0, appendOlder = false) => {
      if (!appendOlder) setLoadingMessages(true)
      else setLoadingOlder(true)
      try {
        const qs = new URLSearchParams({
          limit: String(MESSAGE_PAGE_SIZE),
          offset: String(offset),
        })
        const response = await fetch(`${API_BASE_URL}admin/session/${sessionId}/messages?${qs}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        })

        if (response.ok) {
          const data = await response.json()
          const list: Message[] = data.messages ?? []
          setMessages((prev) => {
            if (appendOlder) {
              const existing = prev[sessionId] ?? []
              const seen = new Set(existing.map((m) => m.id))
              const older = list.filter((m) => !seen.has(m.id))
              return { ...prev, [sessionId]: [...older, ...existing] }
            }
            return { ...prev, [sessionId]: list }
          })
          setMsgPagination((prev) => ({
            ...prev,
            [sessionId]: {
              total: typeof data.total === 'number' ? data.total : list.length,
              hasMoreOlder: Boolean(data.has_more_older),
              nextOffset: data.next_offset ?? null,
            },
          }))
        }
      } catch (error) {
        console.error('Error fetching messages:', error)
      } finally {
        if (!appendOlder) setLoadingMessages(false)
        else setLoadingOlder(false)
      }
    },
    []
  )

  useEffect(() => {
    if (selectedSession) {
      void fetchMessages(selectedSession, 0, false)
    }
  }, [selectedSession, fetchMessages])

  const loadOlderMessages = useCallback(() => {
    if (!selectedSession) return
    const meta = msgPagination[selectedSession]
    if (!meta?.hasMoreOlder || meta.nextOffset == null || loadingOlder) return
    void fetchMessages(selectedSession, meta.nextOffset, true)
  }, [selectedSession, msgPagination, loadingOlder, fetchMessages])

  // Prevent background scroll when modal is open
  useEffect(() => {
    // Store original overflow style
    const originalOverflow = document.body.style.overflow
    const originalPaddingRight = document.body.style.paddingRight
    
    // Get scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden'
    
    // Add padding to prevent layout shift from scrollbar disappearing
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }
    
    // Restore on cleanup
    return () => {
      document.body.style.overflow = originalOverflow
      document.body.style.paddingRight = originalPaddingRight
    }
  }, [])

  const fetchSessions = useCallback(async (offset = 0, append = false) => {
    if (!append) setLoading(true)
    else setLoadingMoreSessions(true)
    try {
      const qs = new URLSearchParams({
        limit: String(SESSION_PAGE_SIZE),
        offset: String(offset),
      })
      const response = await fetch(`${API_BASE_URL}admin/user/${userId}/sessions?${qs}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        const list: Session[] = data.sessions ?? []
        setSessions((prev) => (append ? [...prev, ...list.filter((s) => !prev.some((p) => p.id === s.id))] : list))
        setSessionPagination({
          total: typeof data.total === 'number' ? data.total : list.length,
          hasMore: Boolean(data.has_more),
          nextOffset: data.next_offset ?? null,
        })
        if (list.length > 0 && !append) {
          const pick =
            initialSessionId && list.some((s: Session) => s.id === initialSessionId)
              ? initialSessionId
              : list[0].id
          setSelectedSession(pick)
        }
      }
    } catch (error) {
      console.error('Error fetching sessions:', error)
    } finally {
      if (!append) setLoading(false)
      else setLoadingMoreSessions(false)
    }
  }, [userId, initialSessionId])

  const loadMoreSessions = useCallback(() => {
    if (!sessionPagination.hasMore || sessionPagination.nextOffset == null || loadingMoreSessions) return
    void fetchSessions(sessionPagination.nextOffset, true)
  }, [sessionPagination, loadingMoreSessions, fetchSessions])

  const currentSession = sessions.find(s => s.id === selectedSession)
  const effectiveSessionName = currentSession?.name || initialSessionName || selectedSession || 'Session'
  const effectiveSessionCreatedAt = currentSession?.created_at || initialSessionCreatedAt || null
  const currentMessages = selectedSession ? messages[selectedSession] : []
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Filter sessions based on deletion status
  const filteredSessions = showDeletedSessions 
    ? sessions 
    : sessions.filter(session => session.is_deleted !== 'true')

  const deletedSessionsCount = sessions.filter(session => session.is_deleted === 'true').length
  const activeSessionsCount = sessions.filter(session => session.is_deleted !== 'true').length

  // Scroll to bottom on initial load / new session — not when prepending older messages
  useEffect(() => {
    if (loadingOlder) return
    if (messagesEndRef.current && currentMessages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }, [currentMessages, selectedSession, loadingOlder])

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/65 backdrop-blur-[2px] flex items-center justify-center p-4 overflow-hidden"
      onClick={(e) => {
        // Close modal when clicking backdrop
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div
        className="w-full max-w-7xl h-[88vh] bg-white dark:bg-gray-900 rounded-xl shadow-2xl ring-1 ring-black/10 dark:ring-white/10 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2 text-gray-900 dark:text-white">
                <MessageSquare className="h-5 w-5 text-indigo-600" />
                Chat history — {displayName}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{userEmail}</p>
              {username && username.trim() !== displayName.trim() && (
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">Username: {username}</p>
              )}
            </div>
            <AdminPanelButton type="button" size="icon" onClick={onClose} title="Close">
              <X className="h-5 w-5" />
            </AdminPanelButton>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar - Sessions List */}
          {showSessionsList && (
          <div className="w-72 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex flex-col">
            <div className="px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Chat Sessions ({activeSessionsCount + deletedSessionsCount}
                    {sessionPagination.total > activeSessionsCount + deletedSessionsCount
                      ? ` / ${sessionPagination.total}`
                      : ''}
                    )
                  </h3>
                  {deletedSessionsCount > 0 && (
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                        {activeSessionsCount} active
                      </Badge>
                      <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400">
                        {deletedSessionsCount} deleted
                      </Badge>
                    </div>
                  )}
                </div>
                {deletedSessionsCount > 0 && (
                  <AdminPanelButton
                    type="button"
                    size="sm"
                    onClick={() => setShowDeletedSessions(!showDeletedSessions)}
                  >
                    {showDeletedSessions ? (
                      <>
                        <EyeOff className="h-3 w-3" />
                        Hide Deleted
                      </>
                    ) : (
                      <>
                        <Eye className="h-3 w-3" />
                        Show Deleted
                      </>
                    )}
                  </AdminPanelButton>
                )}
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="h-48 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                </div>
              ) : filteredSessions.length > 0 ? (
                <div className="p-1.5 space-y-1">
                  {filteredSessions.map((session) => {
                    const isDeleted = session.is_deleted === 'true'
                    return (
                      <button
                        key={session.id}
                        onClick={() => setSelectedSession(session.id)}
                        className={`w-full p-2 rounded-lg text-left transition-all relative ${
                          selectedSession === session.id
                            ? isDeleted 
                              ? 'bg-orange-600 text-white shadow-md'
                              : 'bg-indigo-600 text-white shadow-md'
                            : isDeleted
                              ? 'bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 text-gray-900 dark:text-white border border-orange-200 dark:border-orange-800'
                              : 'bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'
                        }`}
                      >
                        {isDeleted && (
                          <div className="absolute top-1 right-1">
                            <Trash2 className="h-3 w-3 text-orange-600 dark:text-orange-400" />
                          </div>
                        )}
                        <div className="flex items-start gap-2">
                          <MessageSquare className={`h-4 w-4 mt-1 flex-shrink-0 ${
                            selectedSession === session.id 
                              ? 'text-white' 
                              : isDeleted 
                                ? 'text-orange-600 dark:text-orange-400'
                                : 'text-indigo-600'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium text-[12px] truncate ${
                              selectedSession === session.id ? 'text-white' : 'text-gray-900 dark:text-white'
                            }`}>
                              {session.name}
                              {isDeleted && (
                                <span className="ml-1 text-xs text-orange-600 dark:text-orange-400">
                                  (Deleted)
                                </span>
                              )}
                            </p>
                            <div className={`flex items-center gap-2 mt-1 text-[11px] ${
                              selectedSession === session.id ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              <Clock className="h-3 w-3" />
                              <span>{formatISTDate(session.created_at)}</span>
                              {isDeleted && session.deleted_at && (
                                <span className="text-orange-600 dark:text-orange-400">
                                  • Deleted: {formatISTDate(session.deleted_at)}
                                </span>
                              )}
                            </div>
                            <p className={`text-[11px] mt-1 ${
                              selectedSession === session.id ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {session.message_count} messages
                              {session.deleted_count > 0 && (
                                <span className="ml-2 text-orange-500">
                                  ({session.deleted_count} regenerated)
                                </span>
                              )}
                            </p>
                            {(session.total_cost != null && session.total_cost > 0) ||
                            (session.total_input_tokens != null && session.total_input_tokens > 0) ? (
                              <p
                                className={`text-[11px] mt-0.5 ${
                                  selectedSession === session.id ? 'text-indigo-100/90' : 'text-gray-500 dark:text-gray-400'
                                }`}
                              >
                                {(session.total_input_tokens ?? 0).toLocaleString()} in /{' '}
                                {(session.total_output_tokens ?? 0).toLocaleString()} out
                                {session.total_cost != null && session.total_cost > 0 && (
                                  <> · ${Number(session.total_cost).toFixed(4)}</>
                                )}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                  {sessionPagination.hasMore && (
                    <div className="pt-1 pb-1 flex justify-center">
                      <AdminPanelButton
                        type="button"
                        size="sm"
                        disabled={loadingMoreSessions}
                        onClick={loadMoreSessions}
                      >
                        {loadingMoreSessions ? 'Loading…' : 'Load more sessions'}
                      </AdminPanelButton>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-gray-500">
                  <p className="text-sm">No sessions found</p>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Chat Area */}
          <div className="flex-1 flex flex-col bg-white dark:bg-gray-900">
            {selectedSession ? (
              <>
                {/* Chat Header */}
                <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <h3 className="font-semibold text-[13px] text-gray-900 dark:text-white">
                    {effectiveSessionName}
                  </h3>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                    {effectiveSessionCreatedAt ? formatISTDate(effectiveSessionCreatedAt) : ''}
                  </p>
                  {selectedSession && msgPagination[selectedSession] && (
                    <p className="text-[10px] text-gray-500 dark:text-gray-500 mt-0.5">
                      Showing {currentMessages.length} of {msgPagination[selectedSession].total} messages
                      {msgPagination[selectedSession].hasMoreOlder ? ' · older messages not loaded yet' : ''}
                    </p>
                  )}
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
                  {loadingMessages ? (
                    <div className="h-full flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                    </div>
                  ) : currentMessages && currentMessages.length > 0 ? (
                    <div className="space-y-2.5 max-w-4xl mx-auto">
                      {selectedSession &&
                        msgPagination[selectedSession]?.hasMoreOlder && (
                          <div className="flex justify-center pb-1">
                            <AdminPanelButton
                              type="button"
                              size="sm"
                              disabled={loadingOlder}
                              onClick={loadOlderMessages}
                            >
                              {loadingOlder ? 'Loading older…' : 'Load older messages'}
                            </AdminPanelButton>
                          </div>
                        )}
                      {currentMessages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex gap-3 ${
                            message.role === 'user' ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          {message.role === 'assistant' && (
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--navy)] flex items-center justify-center shadow-md mt-1">
                              <Bot className="h-4 w-4 text-[var(--gold)]" />
                            </div>
                          )}
                          <div
                            className={`max-w-[72%] rounded-[12px] p-2.5 shadow-sm ${
                              message.is_deleted === 'true'
                                ? 'bg-orange-100 dark:bg-orange-900/20 border-2 border-orange-300 dark:border-orange-700 text-orange-800 dark:text-orange-200'
                                : message.role === 'user'
                                ? 'bg-[var(--navy)] text-white'
                                : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            {message.is_deleted === 'true' && (
                              <div className="text-xs font-semibold text-orange-600 dark:text-orange-400 mb-2">
                                🔄 REGENERATED MESSAGE
                              </div>
                            )}
                            {message.is_edited === 'true' && (
                              <div className="text-xs font-semibold text-white mb-2">
                                ✏️ EDITED MESSAGE
                              </div>
                            )}
                            <div className="text-[12px] leading-6">
                              <MarkdownMessage content={message.content} role={message.role} />
                            </div>
                            <p className={`text-[11px] mt-1.5 ${
                              message.is_deleted === 'true'
                                ? 'text-orange-600 dark:text-orange-400'
                                : message.role === 'user'
                                ? 'text-indigo-100'
                                : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {formatISTDate(message.timestamp)}
                              {message.deleted_at && (
                                <span className="ml-2 text-orange-500">
                                  (regenerated: {formatISTDate(message.deleted_at)})
                                </span>
                              )}
                              {message.edited_at && (
                                <span className="ml-2 text-gray-200">
                                  (edited: {formatISTDate(message.edited_at)})
                                </span>
                              )}
                              {message.role === 'assistant' &&
                                (message.cost != null ||
                                  message.input_tokens != null ||
                                  message.output_tokens != null) && (
                                  <span className="block mt-1 opacity-90">
                                    {message.input_tokens != null && `${message.input_tokens} in`}
                                    {message.input_tokens != null && message.output_tokens != null && ' · '}
                                    {message.output_tokens != null && `${message.output_tokens} out`}
                                    {message.cost != null && ` · $${Number(message.cost).toFixed(6)}`}
                                  </span>
                                )}
                            </p>
                          </div>
                          {message.role === 'user' && (
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--bg2)] border border-[var(--bd)] flex items-center justify-center shadow-sm mt-1">
                              <User className="h-4 w-4 text-[var(--navy)]" />
                            </div>
                          )}
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-500">
                      <p className="text-sm">No messages in this session</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <p className="text-sm">Select a session to view messages</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

